import type { Root } from "../../domain/app-types.ts";
import type { Category, CollectionViewMode, TaskTemplate, Todo } from "../../domain/entities.ts";

export interface TaskDraftOptions {
  rootId: string;
  category?: Category;
  collectionView?: CollectionViewMode;
  rootDefaults?: Root["task_defaults"];
  template?: TaskTemplate;
  activeCycle: string | null;
  cycleView: boolean;
  now: number;
  today: string;
  randomId(): string;
  addDays(date: string, days: number): string;
}

export interface TaskDraftFactoryCapabilities {
  epicId?(): string | null;
  activeRootId(): string | null;
  activeCategoryId(): string | null;
  categories(): Category[];
  collectionView(category: Category): CollectionViewMode;
  roots(): Root[];
  templates(): TaskTemplate[];
  activeCycle(): string | null;
  cycleView(): boolean;
  now(): number;
  today(): string;
  randomId(): string;
  addDays(date: string, days: number): string;
}

export function createTaskDraftFactory(capabilities: TaskDraftFactoryCapabilities): (categoryId?: string) => Todo {
  return categoryId => {
    const rootId = capabilities.activeRootId();
    if (!rootId) throw new Error("Cannot create a task draft without an active space");
    const category = capabilities.categories().find(item => item.id === (categoryId??capabilities.activeCategoryId()) && item.rootId===rootId && !item.deleted && !item.archivedAt);
    const rootDefaults = capabilities.roots().find(item => item.id === rootId)?.task_defaults || {};
    const template = capabilities.templates().find(item => item.id === (category?.defaultTemplateId || rootDefaults.template_id));
    const draft = createTaskDraft({ rootId, category, collectionView: category ? capabilities.collectionView(category) : undefined, rootDefaults, template, activeCycle: capabilities.activeCycle(), cycleView: capabilities.cycleView(), now: capabilities.now(), today: capabilities.today(), randomId: capabilities.randomId, addDays: capabilities.addDays });
    const epicId = capabilities.epicId?.();
    if (epicId) draft.epicId = epicId;
    return draft;
  };
}

export function createTaskDraft(options: TaskDraftOptions): Todo {
  const { category, rootDefaults = {} } = options;
  const template = options.template?.purpose === "note" ? undefined : options.template;
  const firstColumn = (options.collectionView || category?.viewType) === "board" ? category?.boardColumns?.[0] : null;
  const defaultTags = category?.defaultTags?.length ? category.defaultTags : rootDefaults.tags || [];
  const columnTags = firstColumn ? [firstColumn] : defaultTags;
  const dueDate = template?.dueOffsetDays != null
    ? options.addDays(options.today, template.dueOffsetDays)
    : category?.defaultDueOffsetDays != null
      ? options.addDays(options.today, category.defaultDueOffsetDays)
      : rootDefaults.due_offset_days != null ? options.addDays(options.today, rootDefaults.due_offset_days) : null;
  return {
    ...(template?.customFields ? { customFields: structuredClone(template.customFields), customFieldSchema: structuredClone(template.customFieldSchema || {}) } : {}),
    id: options.randomId(), rootId: options.rootId, title: template?.title || "", description: template?.description || "", type: template?.type || category?.defaultTaskType || rootDefaults.task_type || "text", subtasks: structuredClone(template?.subtasks || []), position: options.now, pinned: false,
    completed: false, completedAt: null, priority: template?.priority || category?.defaultPriority || rootDefaults.priority || "none", dueDate, startDate: null, startAt: null, endAt: null, scheduleMode: "fixed", scheduleTimeZone: null, wallStartAt: null, wallEndAt: null, estimatedMinutes: null, reminderAt: null, reminders: [],
    recurrence: null, recurrenceSeriesId: null, recurrenceOccurrence: 0, recurrenceAnchorDate: null,
    comments: [], categoryId: category?.id || null, parentTaskId: null, assignedTo: [...(category?.defaultAssigneeIds?.length ? category.defaultAssigneeIds : rootDefaults.assignee_ids || [])], tags: Object.fromEntries(columnTags.map(tag => [tag, true])),
    tagOrder: columnTags, dependencyIds: [], relationships: [], cycleId: options.cycleView && options.activeCycle ? options.activeCycle : null,
    externalCalendars: [], archivedAt: null, deleted: false, createdAt: options.now, updatedAt: options.now,
  };
}
